/**
 * CreatePipelineSlideout - Create a new deal pipeline with custom stages
 *
 * Provides a slideout for creating pipelines with named stages, drag-and-drop
 * reordering, and probability editing. Uses StageListEditor for the shared
 * stage list layout.
 *
 * Uses FormProvider for validation state management — touched tracking, error
 * computation, and deferred error display are all handled by the form context.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { FormProvider, useFormContext } from '@/lib/base/forms/form-context';
import { FormField } from '@/lib/base/forms/form-field';
import type { FieldConfig } from '@/lib/base/forms/hooks/useFormErrors';
import { InputBase } from '@/lib/base/forms/input/input';
import { InputGroup, InputPrefix } from '@/lib/base/forms/input/input-group';
import { SubmitButton } from '@/lib/base/forms/submit-button';
import { TextArea } from '@/lib/base/forms/textarea/textarea';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import HorizontalTabs from '@/lib/base/tabs/HorizontalTabs';
import {
  buildNewStageTerminalPayload,
  createIsTerminalColumn,
} from '@/lib/crm/components/stages/stageColumns';
import { StageDescriptionField } from '@/lib/crm/components/stages/StageDescriptionField';
import type {
  StageColumn,
  StageItem,
  StageValidationErrors,
} from '@/lib/crm/components/stages/StageListEditor';
import { StageListEditor } from '@/lib/crm/components/stages/StageListEditor';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import {
  CardFieldsEditorBody,
  type CardLayoutTabProps,
} from '@/lib/crm/pipeline/components/CardFieldsEditorBody';
import { useCardFieldsDraft } from '@/lib/crm/pipeline/hooks/useCardFieldsDraft';
import { usePipelineCardConfig } from '@/lib/crm/pipeline/hooks/usePipelineCardConfig';
import { buildDefaultCardConfig } from '@/lib/crm/pipeline/utils/cardConfigDefaults';
import {
  buildFieldPickerOptions,
  buildSupplementalPropertyOptions,
} from '@/lib/crm/pipeline/utils/cardConfigOptions';
import { apiPost } from '@/lib/http/client';
import type { PipelineTemplate } from '@/lib/proposals/core/types';
import { usePipelineTemplates } from '@/lib/proposals/templates/usePipelineTemplates';

// =============================================================================
// Types
// =============================================================================

export interface CreatePipelineSlideoutProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: number;
  /**
   * Base entity type the pipeline belongs to (e.g. `ahoy_deal`, `ahoy_note`).
   * `undefined` is treated as the deal pipeline for backwards compatibility.
   * Controls whether the deal-only "Win %" column is shown and which default
   * stages are seeded.
   */
  entityType?: string;
  /**
   * Human-readable singular label for the entity type (e.g. "Deal", "Note").
   * Used for the slideout title and submit button copy. Defaults to a generic
   * "pipeline" when omitted.
   */
  entityLabel?: string;
  existingPipelineNames?: string[];
}

/**
 * Deal pipelines are the only type that tracks win probability per stage.
 * Everything else (notes, custom entities) uses a probability-free stage list.
 */
function isDealEntityType(entityType: string | undefined): boolean {
  return !entityType || entityType === 'ahoy_deal';
}

interface StageItemWithProbability extends StageItem {
  probability: number; // 0-1 for API
  order: number; // 1-based
  description?: string;
  is_terminal?: boolean;
  /**
   * Whether the user explicitly interacted with the Final toggle this session.
   * Lets an unchecked seeded-final stage (e.g. Closed Won) persist an explicit
   * false instead of being dropped and left unset.
   */
  terminalTouched?: boolean;
}

// =============================================================================
// Factory function for default stages (fresh UUIDs each time)
// =============================================================================

function createDefaultStages(): StageItemWithProbability[] {
  return [
    {
      id: crypto.randomUUID(),
      name: 'Qualification',
      probability: 0.1,
      order: 1,
      color: '#7A5AF8',
      is_terminal: false,
    },
    {
      id: crypto.randomUUID(),
      name: 'Needs Analysis',
      probability: 0.25,
      order: 2,
      color: '#0BA5EC',
      is_terminal: false,
    },
    {
      id: crypto.randomUUID(),
      name: 'Proposal',
      probability: 0.5,
      order: 3,
      color: '#6172F3',
      is_terminal: false,
    },
    {
      id: crypto.randomUUID(),
      name: 'Negotiation',
      probability: 0.75,
      order: 4,
      color: '#EE46BC',
      is_terminal: false,
    },
    {
      id: crypto.randomUUID(),
      name: 'Closed Won',
      probability: 1.0,
      order: 5,
      color: '#16B364',
      is_terminal: true,
    },
    {
      id: crypto.randomUUID(),
      name: 'Closed Lost',
      probability: 0.0,
      order: 6,
      color: '#F04438',
      is_terminal: true,
    },
  ];
}

/**
 * Default stages for non-deal pipelines (notes, custom entities). These have no
 * meaningful win probability, so they seed a neutral To Do → Completed flow
 * mirroring the seeded note pipeline. Probability is kept at 0 to satisfy the
 * shared StageItemWithProbability shape but is never surfaced or submitted.
 */
function createGenericDefaultStages(): StageItemWithProbability[] {
  return [
    {
      id: crypto.randomUUID(),
      name: 'To Do',
      probability: 0,
      order: 1,
      color: '#7A5AF8',
    },
    {
      id: crypto.randomUUID(),
      name: 'In Progress',
      probability: 0,
      order: 2,
      color: '#0BA5EC',
    },
    {
      id: crypto.randomUUID(),
      name: 'Completed',
      probability: 0,
      order: 3,
      color: '#17B26A',
    },
  ];
}

function getDefaultStages(isDeal: boolean): StageItemWithProbability[] {
  return isDeal ? createDefaultStages() : createGenericDefaultStages();
}

// Sentinel for the "no template" option in the picker.
const BLANK_TEMPLATE_KEY = 'BLANK';
// Template applied by default for deal pipelines when available.
const DEFAULT_TEMPLATE_KEY = 'SPICED';

/**
 * Map a pipeline template's `stages_properties` (CRM property-value dicts keyed
 * by property type name, values in stored string form) into the editor's stage
 * shape. `ahoy_probability` is a 0–1 fraction string; `ahoy_order` is derived
 * from list position.
 */
function templateToStages(
  template: PipelineTemplate
): StageItemWithProbability[] {
  return template.stages_properties.map((props, index) => {
    const probabilityRaw = props.ahoy_probability;
    const probability =
      probabilityRaw == null
        ? 0
        : Number.parseFloat(String(probabilityRaw)) || 0;
    const color = props.ahoy_color ? String(props.ahoy_color) : undefined;
    return {
      id: crypto.randomUUID(),
      name: String(props.ahoy_name ?? ''),
      probability,
      order: index + 1,
      color,
      description: props.ahoy_description ? String(props.ahoy_description) : '',
      is_terminal:
        props.ahoy_is_terminal === 'true' || props.ahoy_is_terminal === true,
    };
  });
}

// =============================================================================
// Field key helper — composite key for per-stage, per-field touch tracking
// =============================================================================

const makeFieldKey = (id: string, field: string) => `${id}:${field}`;
const makeNameFieldKey = (id: string) => makeFieldKey(id, 'name');

// =============================================================================
// Probability column definition
// =============================================================================

function createProbabilityColumn(
  onProbabilityChange: (id: string, probability: number) => void,
  isSaving: boolean
): StageColumn<StageItemWithProbability> {
  return {
    id: 'probability',
    header: 'Win %',
    width: 'w-24',
    align: 'center',
    renderCell: (stage, { disabled }) => {
      const displayPercentage = Math.round(stage.probability * 100);

      const handleChange = (value: string) => {
        const numValue = parseInt(value, 10);
        if (isNaN(numValue)) {
          onProbabilityChange(stage.id, 0);
        } else {
          onProbabilityChange(stage.id, numValue / 100);
        }
      };

      return (
        <div data-form-field={makeFieldKey(stage.id, 'probability')}>
          <InputGroup
            aria-label='Win probability'
            value={String(displayPercentage)}
            onChange={handleChange}
            isDisabled={disabled || isSaving}
            trailingAddon={
              <InputPrefix isDisabled={disabled || isSaving}>%</InputPrefix>
            }
          >
            <InputBase type='number' />
          </InputGroup>
        </div>
      );
    },
    renderDragPreview: stage => (
      <span className='text-sm text-fg-quaternary'>
        {Math.round(stage.probability * 100)}%
      </span>
    ),
  };
}

// =============================================================================
// TemplateOption — a single selectable template chip in the picker
// =============================================================================

interface TemplateOptionProps {
  templateKey: string;
  label: string;
  isSelected: boolean;
  disabled: boolean;
  onSelect: (key: string) => void;
}

function TemplateOption({
  templateKey,
  label,
  isSelected,
  disabled,
  onSelect,
}: TemplateOptionProps) {
  const handleClick = useCallback(
    () => onSelect(templateKey),
    [onSelect, templateKey]
  );
  return (
    <Button
      size='sm'
      color={isSelected ? 'primary' : 'secondary'}
      onClick={handleClick}
      isDisabled={disabled}
    >
      {label}
    </Button>
  );
}

// ── Tab configuration ────────────────────────────────────────────────────────

const PIPELINE_TABS = [
  { id: 'stages', label: 'Stages' },
  { id: 'card-layout', label: 'Card Layout' },
];

// =============================================================================
// PipelineFormContent — inner component that accesses FormContext
//
// Separated from the outer component so it can call useFormContext() as a
// descendant of FormProvider. Renders the slideout body and footer.
// =============================================================================

interface PipelineFormContentProps {
  apiError: string | null;
  title: string;
  submitLabel: string;
  templates: PipelineTemplate[];
  selectedTemplateKey: string;
  onSelectTemplate: (key: string) => void;
  pipelineName: string;
  onPipelineNameChange: (name: string) => void;
  pipelineDescription: string;
  onPipelineDescriptionChange: (description: string) => void;
  stages: StageItemWithProbability[];
  columns: StageColumn<StageItemWithProbability>[];
  isSaving: boolean;
  onNameChange: (id: string, name: string) => void;
  onColorChange: (id: string, color: string | null) => void;
  onStageDescriptionChange: (id: string, description: string) => void;
  onDelete: (id: string) => void;
  onAddStage: () => void;
  onReorder: (stages: StageItemWithProbability[]) => void;
  onClose: () => void;
  onSave: () => Promise<void>;
  announcement: string;
  /** Card layout tab props — only provided for pipeline entity types */
  cardLayoutTab?: CardLayoutTabProps;
}

function PipelineFormContent({
  apiError,
  title,
  submitLabel,
  templates,
  selectedTemplateKey,
  onSelectTemplate,
  pipelineName,
  onPipelineNameChange,
  pipelineDescription,
  onPipelineDescriptionChange,
  stages,
  columns,
  isSaving,
  onNameChange,
  onColorChange,
  onStageDescriptionChange,
  onDelete,
  onAddStage,
  onReorder,
  onClose,
  onSave,
  announcement,
  cardLayoutTab,
}: PipelineFormContentProps) {
  const [activeTab, setActiveTab] = useState('stages');
  const handleTabChange = useCallback((tabId: string) => {
    setActiveTab(tabId);
  }, []);
  const { getFieldProps, visibleErrors } = useFormContext();

  // Full-width editable description beneath each stage row.
  const renderStageDetail = useCallback(
    (stage: StageItemWithProbability) => (
      <StageDescriptionField
        stageId={stage.id}
        stageName={stage.name}
        value={stage.description ?? ''}
        disabled={isSaving}
        onChange={onStageDescriptionChange}
      />
    ),
    [isSaving, onStageDescriptionChange]
  );

  const selectedTemplate = useMemo(
    () => templates.find(t => t.key === selectedTemplateKey) ?? null,
    [templates, selectedTemplateKey]
  );

  // Bridge FormContext blur tracking to StageListEditor's (id) signature
  const handleNameBlur = useCallback(
    (id: string) => {
      getFieldProps(makeFieldKey(id, 'name')).onBlur();
    },
    [getFieldProps]
  );

  // Convert FormContext visibleErrors to StageListEditor's validationErrors shape
  const validationErrors = useMemo<StageValidationErrors>(() => {
    const errors: StageValidationErrors = {};
    for (const stage of stages) {
      const nameError = visibleErrors[makeFieldKey(stage.id, 'name')];
      const probError = visibleErrors[makeFieldKey(stage.id, 'probability')];
      if (nameError || probError) {
        errors[stage.id] = {};
        if (nameError) {
          errors[stage.id].name = nameError;
        }
        if (probError) {
          errors[stage.id].probability = probError;
        }
      }
    }
    return errors;
  }, [stages, visibleErrors]);

  const resolveCanDelete = useCallback(
    (_id: string) => stages.length > 1,
    [stages.length]
  );

  const isCardLayoutActive = activeTab === 'card-layout' && !!cardLayoutTab;

  return (
    <>
      <SlideoutMenu.Header onClose={onClose}>
        <h2 className='text-lg font-semibold text-text-primary'>{title}</h2>
      </SlideoutMenu.Header>

      {/* Tab bar — only rendered when card layout tab is available */}
      {cardLayoutTab && (
        <div className='flex-shrink-0 border-b border-[var(--color-border-secondary)] px-5'>
          <HorizontalTabs
            tabs={PIPELINE_TABS}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            noBorder
          />
        </div>
      )}

      <SlideoutMenu.Content>
        {isCardLayoutActive ? (
          <div className='py-4'>
            <CardFieldsEditorBody
              draftState={cardLayoutTab.draftState}
              properties={cardLayoutTab.properties}
              associations={cardLayoutTab.associations}
              entityLabel={cardLayoutTab.entityLabel}
              supplementalOptions={cardLayoutTab.supplementalOptions}
            />
          </div>
        ) : (
          <div className='space-y-4 py-4'>
            {/* API error banner */}
            {apiError && (
              <div className='text-text-error-primary text-sm bg-utility-error-50 border border-utility-error-200 rounded-lg p-3'>
                {apiError}
              </div>
            )}

            {/* Template picker — only shown when the record type has templates */}
            {templates.length > 0 && (
              <div className='space-y-2'>
                <label className='block text-sm font-medium text-text-primary'>
                  Start from a template
                </label>
                <div className='flex flex-wrap gap-2'>
                  {templates.map(template => (
                    <TemplateOption
                      key={template.key}
                      templateKey={template.key}
                      label={template.key}
                      isSelected={selectedTemplateKey === template.key}
                      disabled={isSaving}
                      onSelect={onSelectTemplate}
                    />
                  ))}
                </div>
                {selectedTemplate?.description && (
                  <p className='text-sm text-text-tertiary'>
                    {selectedTemplate.description}
                  </p>
                )}
              </div>
            )}

            {/* Pipeline name input — managed by FormField */}
            <FormField name='pipelineName' label='Pipeline name' isRequired>
              {({ isInvalid, showError }) => (
                <InputGroup
                  aria-label='Pipeline name'
                  value={pipelineName}
                  onChange={onPipelineNameChange}
                  isDisabled={isSaving}
                  isInvalid={isInvalid}
                  showError={showError}
                >
                  <InputBase placeholder='e.g. Enterprise Sales' />
                </InputGroup>
              )}
            </FormField>

            {/* Pipeline description — guides the AI teammate on routing */}
            <TextArea
              label='Description'
              value={pipelineDescription}
              onChange={onPipelineDescriptionChange}
              isDisabled={isSaving}
              rows={3}
              placeholder='Describe what belongs in this pipeline – your AI teammate uses this to route records to the right pipeline.'
              hint='Explain who or what this pipeline is for. Visible to your team and your AI teammate.'
            />

            {/* Stages section */}
            <div>
              <label className='block text-sm font-medium text-text-primary mb-2'>
                Stages
              </label>

              <StageListEditor<StageItemWithProbability>
                stages={stages}
                columns={columns}
                showColumnHeaders
                renderRowFooter={renderStageDetail}
                onNameChange={onNameChange}
                onNameBlur={handleNameBlur}
                onColorChange={onColorChange}
                onRemove={onDelete}
                onReorder={onReorder}
                onAdd={onAddStage}
                disabled={isSaving}
                canDelete={resolveCanDelete}
                validationErrors={validationErrors}
                announcement={announcement}
                formFieldName={makeNameFieldKey}
              />
            </div>
          </div>
        )}
      </SlideoutMenu.Content>

      <SlideoutMenu.Footer>
        {isCardLayoutActive ? (
          <div className='flex items-center justify-between'>
            <Button
              color='link-gray'
              size='sm'
              onClick={cardLayoutTab.onResetCardConfig}
            >
              Reset to default
            </Button>
            <div className='flex items-center gap-3'>
              <Button size='sm' color='secondary' onClick={onClose}>
                Cancel
              </Button>
              <Button
                size='sm'
                color='primary'
                onClick={cardLayoutTab.onSaveCardConfig}
                isDisabled={!cardLayoutTab.draftState.isDirty}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className='flex justify-end gap-3'>
            <SubmitButton
              size='sm'
              color='primary'
              onClick={onSave}
              isDisabled={isSaving}
            >
              {isSaving ? 'Creating…' : submitLabel}
            </SubmitButton>
          </div>
        )}
      </SlideoutMenu.Footer>
    </>
  );
}

// =============================================================================
// CreatePipelineSlideout - Main component
// =============================================================================

export function CreatePipelineSlideout({
  isOpen,
  onOpenChange,
  orgId,
  entityType,
  entityLabel,
  existingPipelineNames,
}: CreatePipelineSlideoutProps) {
  const queryClient = useQueryClient();

  // Deal pipelines are the only type with a win-probability column. Other types
  // (notes, custom entities) get a probability-free stage list.
  const isDeal = isDealEntityType(entityType);

  // Templates are keyed by record entity type on the backend, so this is not
  // deal-specific: a record type with no templates just returns an empty list
  // and the picker stays hidden. `entityType` is undefined only for the legacy
  // deal default, so resolve that one case here.
  const recordEntityType = entityType ?? 'ahoy_deal';
  const { templates } = usePipelineTemplates(orgId, recordEntityType);

  // Type-aware copy: "Create deal pipeline" / "Create note pipeline" etc.
  const title = entityLabel
    ? `Create ${entityLabel.toLowerCase()} pipeline`
    : 'Create pipeline';

  // State
  const [pipelineName, setPipelineName] = useState('');
  const [pipelineDescription, setPipelineDescription] = useState('');
  const [stages, setStages] = useState<StageItemWithProbability[]>(() =>
    getDefaultStages(isDeal)
  );
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [selectedTemplateKey, setSelectedTemplateKey] =
    useState<string>(BLANK_TEMPLATE_KEY);
  // Baseline the dirty-check compares against — refreshed whenever a template
  // (or Blank) is applied so selecting one doesn't itself read as "dirty".
  const baselineStagesRef = useRef<StageItemWithProbability[]>(stages);

  // Whether the stage list diverges from the baseline the active template
  // established. Tracked separately from name/description so the default-template
  // prefill can run as long as the stages themselves are still untouched.
  const stagesAreDirty = useMemo(() => {
    const baseline = baselineStagesRef.current;
    if (stages.length !== baseline.length) {
      return true;
    }
    return stages.some((stage, i) => {
      const baselineStage = baseline[i];
      return (
        !baselineStage ||
        stage.name !== baselineStage.name ||
        stage.probability !== baselineStage.probability ||
        stage.order !== baselineStage.order ||
        stage.color !== baselineStage.color ||
        (stage.description ?? '') !== (baselineStage.description ?? '') ||
        (stage.is_terminal ?? false) !== (baselineStage.is_terminal ?? false)
      );
    });
  }, [stages]);

  // Check if form is dirty
  const isDirty = useMemo(() => {
    if (pipelineName.trim() !== '' || pipelineDescription.trim() !== '') {
      return true;
    }
    return stagesAreDirty;
  }, [pipelineName, pipelineDescription, stagesAreDirty]);

  // =========================================================================
  // FormProvider config — field validation + current values
  //
  // FormProvider manages touched state, error computation, and deferred
  // display. It remounts when the slideout closes (unmounting the render
  // function), so touched state resets automatically.
  // =========================================================================

  const fieldConfig = useMemo(() => {
    const config: Record<string, FieldConfig> = {
      pipelineName: {
        validate: (v: unknown) => {
          if (!String(v ?? '').trim()) {
            return 'Pipeline name is required';
          }
          const trimmedLower = String(v ?? '')
            .trim()
            .toLowerCase();
          if (
            existingPipelineNames?.some(
              name => name.trim().toLowerCase() === trimmedLower
            )
          ) {
            return 'A pipeline with this name already exists';
          }
          return undefined;
        },
      },
    };

    // Build stage name list for duplicate detection. Each stage's validate
    // function checks against ALL other stages by index.
    const stageNames = stages.map(s => s.name.toLowerCase().trim());

    for (let i = 0; i < stages.length; i++) {
      const stage = stages[i];
      const nameKey = makeFieldKey(stage.id, 'name');

      config[nameKey] = {
        validate: (v: unknown) => {
          const val = String(v ?? '').trim();
          if (!val) {
            return 'Name is required';
          }
          const lowerVal = val.toLowerCase();
          const isDuplicate = stageNames.some(
            (name, idx) => idx !== i && name === lowerVal
          );
          if (isDuplicate) {
            return 'Duplicate stage name';
          }
          return undefined;
        },
      };

      // Probability is a deal-only concern — skip validation for other types.
      if (isDeal) {
        const probKey = makeFieldKey(stage.id, 'probability');
        config[probKey] = {
          validate: (v: unknown) => {
            const p = Number(v);
            if (p < 0 || p > 1) {
              return 'Must be 0-100%';
            }
            return undefined;
          },
        };
      }
    }

    return config;
  }, [stages, existingPipelineNames, isDeal]);

  const formValues = useMemo(() => {
    const values: Record<string, unknown> = {
      pipelineName,
    };
    for (const stage of stages) {
      values[makeFieldKey(stage.id, 'name')] = stage.name;
      if (isDeal) {
        values[makeFieldKey(stage.id, 'probability')] = stage.probability;
      }
    }
    return values;
  }, [pipelineName, stages, isDeal]);

  const fieldLabels = useMemo(() => {
    const labels: Record<string, string> = {
      pipelineName: 'Pipeline name',
    };
    for (const stage of stages) {
      const name = stage.name || '(unnamed)';
      labels[makeFieldKey(stage.id, 'name')] = `Stage "${name}"`;
      if (isDeal) {
        labels[makeFieldKey(stage.id, 'probability')] =
          `Probability for "${name}"`;
      }
    }
    return labels;
  }, [stages, isDeal]);

  // =========================================================================
  // Handlers — all stable useCallback references
  // =========================================================================

  const handleNameChange = useCallback((id: string, name: string) => {
    setStages(prev => prev.map(s => (s.id === id ? { ...s, name } : s)));
  }, []);

  const handleProbabilityChange = useCallback(
    (id: string, probability: number) => {
      setStages(prev =>
        prev.map(s => (s.id === id ? { ...s, probability } : s))
      );
    },
    []
  );

  const handleColorChange = useCallback((id: string, color: string | null) => {
    setStages(prev =>
      prev.map(s => (s.id === id ? { ...s, color: color ?? '' } : s))
    );
  }, []);

  const handleIsTerminalChange = useCallback(
    (id: string, isTerminal: boolean) => {
      setStages(prev =>
        prev.map(s =>
          s.id === id
            ? { ...s, is_terminal: isTerminal, terminalTouched: true }
            : s
        )
      );
    },
    []
  );

  const handleStageDescriptionChange = useCallback(
    (id: string, description: string) => {
      setStages(prev =>
        prev.map(s => (s.id === id ? { ...s, description } : s))
      );
    },
    []
  );

  const handleDelete = useCallback(
    (id: string) => {
      setStages(prev => {
        const filtered = prev.filter(s => s.id !== id);
        return filtered.map((s, i) => ({ ...s, order: i + 1 }));
      });
      const stageName = stages.find(s => s.id === id)?.name || 'Stage';
      setAnnouncement(`${stageName} deleted`);
    },
    [stages]
  );

  const handleAddStage = useCallback(() => {
    setStages(prev => {
      const newStage: StageItemWithProbability = {
        id: crypto.randomUUID(),
        name: '',
        probability: 0,
        order: prev.length + 1,
      };
      return [...prev, newStage];
    });
    setAnnouncement('New stage added');
  }, []);

  const handleReorder = useCallback((reordered: StageItemWithProbability[]) => {
    const withOrder = reordered.map((s, i) => ({ ...s, order: i + 1 }));
    setStages(withOrder);
    setAnnouncement('Stage reordered');
  }, []);

  // Build probability column — deal pipelines only. Other types render a
  // name-and-color-only stage list.
  const columns = useMemo<StageColumn<StageItemWithProbability>[]>(
    () => [
      ...(isDeal
        ? [createProbabilityColumn(handleProbabilityChange, isSaving)]
        : []),
      createIsTerminalColumn(handleIsTerminalChange, isSaving),
    ],
    [handleProbabilityChange, handleIsTerminalChange, isSaving, isDeal]
  );

  // Apply a template (or Blank) — replaces the stage list and refreshes the
  // dirty baseline so the selection itself isn't treated as an edit.
  const applyTemplateSelection = useCallback(
    (key: string) => {
      let next: StageItemWithProbability[];
      if (key === BLANK_TEMPLATE_KEY) {
        next = getDefaultStages(isDeal);
      } else {
        const template = templates.find(t => t.key === key);
        next = template ? templateToStages(template) : getDefaultStages(isDeal);
      }
      baselineStagesRef.current = next;
      setStages(next);
      setSelectedTemplateKey(key);
      setAnnouncement(
        key === BLANK_TEMPLATE_KEY
          ? 'Started from a blank pipeline'
          : `Loaded the ${key} template`
      );
    },
    [templates, isDeal]
  );

  // Once templates load, pre-load the recommended one (or the first available)
  // — but only while the stages are still untouched, so we never clobber stage
  // edits. Name/description edits don't block this: the prefill replaces only
  // the stage list, so a user who typed a name before templates resolved still
  // gets the intended default template applied to their stages.
  //
  // Intentionally avoids calling applyTemplateSelection to prevent a stale
  // closure: applyTemplateSelection closes over `templates` via useCallback
  // deps. If it were called here with a stale closure where templates=[],
  // find() would return undefined, falling back to default stages while still
  // setting selectedTemplateKey='SPICED' — blocking any retry.
  useEffect(() => {
    if (templates.length === 0) {
      return;
    }
    if (selectedTemplateKey !== BLANK_TEMPLATE_KEY || stagesAreDirty) {
      return;
    }
    const preferred =
      templates.find(t => t.key === DEFAULT_TEMPLATE_KEY) ?? templates[0];
    const next = templateToStages(preferred);
    baselineStagesRef.current = next;
    setStages(next);
    setSelectedTemplateKey(preferred.key);
    setAnnouncement(`Loaded the ${preferred.key} template`);
  }, [templates, selectedTemplateKey, stagesAreDirty]);

  // Reset to a blank pristine form. The effect above re-applies the default
  // template afterward when one is available.
  const resetForm = useCallback(() => {
    const blank = getDefaultStages(isDeal);
    baselineStagesRef.current = blank;
    setStages(blank);
    setSelectedTemplateKey(BLANK_TEMPLATE_KEY);
    setPipelineName('');
    setPipelineDescription('');
    setApiError(null);
  }, [isDeal]);

  // Save handler
  const handleSave = useCallback(async () => {
    if (stages.length === 0) {
      setApiError('At least one stage is required');
      return;
    }

    setApiError(null);
    setIsSaving(true);

    try {
      await apiPost(`/api/orgs/${orgId}/crm-events/schemas/pipelines/`, {
        name: pipelineName.trim(),
        description: pipelineDescription.trim(),
        stages: stages.map(s => ({
          name: s.name.trim(),
          order: s.order,
          color: s.color || null,
          description: (s.description ?? '').trim(),
          // Persist the user's explicit Final choice once they've toggled it
          // (so unchecking a seeded-final stage like Closed Won sends an
          // explicit false); otherwise omit false so an untouched stage's
          // ahoy_is_terminal stays unset (non-terminal) rather than being
          // written false.
          ...buildNewStageTerminalPayload(s),
          // Win probability is a deal-only field; omit it for other types.
          ...(isDeal ? { probability: s.probability } : {}),
        })),
        entity_type: entityType,
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['pipelines', orgId] }),
        queryClient.invalidateQueries({ queryKey: ['stages', orgId] }),
      ]);

      onOpenChange(false);

      // Reset form state AFTER closing. Touched state resets automatically
      // because FormProvider unmounts when the slideout closes.
      resetForm();
    } catch (err) {
      // Form state preserved — user can retry
      setApiError(
        (err as { response?: { detail?: string } })?.response?.detail ||
          "We couldn't create this pipeline. Try again."
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    pipelineName,
    pipelineDescription,
    stages,
    orgId,
    entityType,
    isDeal,
    queryClient,
    onOpenChange,
    resetForm,
  ]);

  // =========================================================================
  // Card layout config — property/association hooks + card draft state
  // =========================================================================

  const { schema: propertySchemas } = usePropertySchema(
    orgId,
    recordEntityType
  );
  const { associationTypes, associationTypeMap, stageAssociationTypes } =
    useAssociationTypes(orgId);

  const { config: cardConfig, updateConfig: updateCardConfig } =
    usePipelineCardConfig(
      orgId,
      recordEntityType,
      propertySchemas,
      associationTypeMap
    );

  const cardFieldOptions = useMemo(
    () =>
      buildFieldPickerOptions(
        recordEntityType,
        propertySchemas,
        associationTypes,
        stageAssociationTypes
      ),
    [recordEntityType, propertySchemas, associationTypes, stageAssociationTypes]
  );

  const cardDraftState = useCardFieldsDraft(cardConfig);

  // Re-seed card draft each time the slideout opens so it reflects the
  // latest persisted config (the hook's useState only captures the initial
  // value; subsequent opens need an explicit reset).
  const latestCardConfigRef = useRef(cardConfig);
  useEffect(() => {
    latestCardConfigRef.current = cardConfig;
  });
  const resetCardDraft = cardDraftState.resetDraft;

  useEffect(() => {
    if (isOpen) {
      resetCardDraft(latestCardConfigRef.current);
    }
  }, [isOpen, resetCardDraft]);

  const handleSaveCardConfig = useCallback(() => {
    const draft = cardDraftState.getDraft();
    updateCardConfig(draft);
    cardDraftState.resetDraft(draft);
    toast.success('Card layout saved');
    // Card draft is now persisted and clean. If there are no other unsaved
    // changes (stages/name/description), close the slideout. If there ARE
    // unsaved form changes, stay open so the user can save or discard those
    // separately — showing a "discard" dialog right after a successful save
    // is confusing.
    if (!isDirty && !isSaving) {
      onOpenChange(false);
    }
  }, [cardDraftState, updateCardConfig, isDirty, isSaving, onOpenChange]);

  const handleResetCardConfig = useCallback(() => {
    // Draft-only: load defaults into the editor without persisting. The
    // change is committed (and localStorage updated) only when the user
    // clicks Save — so Reset followed by Cancel keeps the original config.
    const defaultConfig = buildDefaultCardConfig(
      recordEntityType,
      propertySchemas,
      associationTypeMap
    );
    cardDraftState.replaceDraft(defaultConfig);
  }, [recordEntityType, propertySchemas, associationTypeMap, cardDraftState]);

  const supplementalOptions = useMemo(
    () =>
      buildSupplementalPropertyOptions(
        cardDraftState.draft,
        propertySchemas,
        cardFieldOptions.properties
      ),
    [cardDraftState.draft, propertySchemas, cardFieldOptions.properties]
  );

  const cardLayoutTab = useMemo<CardLayoutTabProps | undefined>(() => {
    return {
      draftState: cardDraftState,
      properties: cardFieldOptions.properties,
      associations: cardFieldOptions.associations,
      onSaveCardConfig: handleSaveCardConfig,
      onResetCardConfig: handleResetCardConfig,
      entityLabel,
      supplementalOptions,
    };
  }, [
    cardDraftState,
    cardFieldOptions,
    handleSaveCardConfig,
    handleResetCardConfig,
    entityLabel,
    supplementalOptions,
  ]);

  // Combined dirty state: stages/name/description OR unsaved card layout changes
  const isAnythingDirty = isDirty || cardDraftState.isDirty;

  // Raw close — SlideoutMenu owns the unsaved-changes prompt via
  // hasUnsavedChanges/onDiscardUnsavedChanges below, so this only needs to
  // forward the already-guarded close intent.
  const handleSlideoutOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onOpenChange(false);
      }
    },
    [onOpenChange]
  );

  return (
    <SlideoutMenu
      isOpen={isOpen}
      onOpenChange={handleSlideoutOpenChange}
      hasUnsavedChanges={isAnythingDirty}
      onDiscardUnsavedChanges={resetForm}
      minWidth={480}
      maxWidth={800}
      resizable
      dialogAriaLabel={title}
    >
      {({ close }) => (
        <FormProvider
          fields={fieldConfig}
          values={formValues}
          fieldLabels={fieldLabels}
        >
          <PipelineFormContent
            apiError={apiError}
            title={title}
            submitLabel={title}
            templates={templates}
            selectedTemplateKey={selectedTemplateKey}
            onSelectTemplate={applyTemplateSelection}
            pipelineName={pipelineName}
            onPipelineNameChange={setPipelineName}
            pipelineDescription={pipelineDescription}
            onPipelineDescriptionChange={setPipelineDescription}
            stages={stages}
            columns={columns}
            isSaving={isSaving}
            onNameChange={handleNameChange}
            onColorChange={handleColorChange}
            onStageDescriptionChange={handleStageDescriptionChange}
            onDelete={handleDelete}
            onAddStage={handleAddStage}
            onReorder={handleReorder}
            onClose={close}
            onSave={handleSave}
            announcement={announcement}
            cardLayoutTab={cardLayoutTab}
          />
        </FormProvider>
      )}
    </SlideoutMenu>
  );
}
